import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import { Lock, Users, CloudDownload } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DefaultThumbnail from '../../../../assets/recording/WhiteThumbnail.svg';
import type { FileType, FolderType } from '../../../../types';
import FileActionMenu from '../FileActionMenu';
import { pushModal } from '../../../globalModals/globalModalsSlice';
import { RootState } from '../../../../reducers';
import { useCachedThumbnail } from '../../../../hooks/useCachedThumbnail';
import { Card, CardContent } from '../../../../components/ui/card';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../../../../components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../components/ui/tooltip';
import { Skeleton } from '../../../../components/ui/skeleton';
import { Checkbox } from '../../../../components/ui/checkbox';
import { isDemoVideo } from '../../../../constants/demoVideo';

interface FileProps {
  file: FileType;
  isSelected: boolean;
  showCheckbox: boolean;
  onSelect: (file: FileType) => void;
  fileUrl: string;
}

const getCreatedAtString = (createdAt: Date) => {
  const now = moment();
  const created = moment(createdAt);

  if (!created.isValid()) {
    return createdAt;
  }

  if (created.isSame(now, 'day')) {
    return 'Today';
  }

  if (created.isSame(now.subtract(1, 'days'), 'day')) {
    return 'Yesterday';
  }

  if (created.isSame(now.subtract(2, 'days'), 'day')) {
    return '2 days ago';
  }

  if (created.isSame(now.subtract(3, 'days'), 'day')) {
    return '3 days ago';
  }

  return created.format('MMM D, YYYY');
};

const formatDuration = (seconds: number | undefined): string => {
  if (typeof seconds !== 'number') return '0:00';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return hours > 0
    ? `${hours}:${minutes
        .toString()
        .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    : `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Copied from PendingImport for import animations
const ProcessingGridView = ({ name }: { name: string }) => {
  const [message, setMessage] = useState('Processing...');

  useEffect(() => {
    // Start the message cycling sequence
    const messageSequence = async () => {
      setTimeout(() => setMessage('Importing your file..'), 2000);

      setTimeout(() => {
        const cyclingMessages = [
          'This may take a few moments..',
          'Processing your file..',
          'Importing..',
          'Still Working..',
        ];

        let messageIndex = 0;
        const interval = setInterval(() => {
          setMessage(cyclingMessages[messageIndex]);
          messageIndex = (messageIndex + 1) % cyclingMessages.length;
        }, 5000);

        return () => clearInterval(interval);
      }, 5000);
    };

    messageSequence();
  }, []);

  return (
    <>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        @keyframes expandIn {
          0% {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
      <Card className="relative flex flex-col h-full shadow-none bg-transparent border-0 transition-all duration-200 ease-out transform scale-100 hover:scale-102 [container-type:inline-size] animate-[expandIn_0.3s_ease-out]">
        <div className="relative">
          <Skeleton className="w-full aspect-video bg-white dark:bg-black" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2 text-center px-2">
              <CloudDownload
                className="text-5xl opacity-90 text-primary animate-[float_2s_ease-in-out_infinite]"
                size={48}
              />
              <p className="font-semibold text-gray-900 dark:text-gray-100 [@container_(max-width:_250px)]:hidden">
                {message}
              </p>
              <p className="text-sm text-gray-900 dark:text-gray-100 opacity-80 [@container_(max-width:_250px)]:hidden">
                We&apos;ll email you when ready
              </p>
            </div>
          </div>
        </div>
        <CardContent className="flex-grow flex flex-col justify-start py-4 px-0 last:pb-4 bg-transparent">
          <div className="flex items-start">
            <Avatar className="w-8 h-8 bg-primary transition-transform duration-200 ease-out">
              <AvatarFallback className="bg-primary">
                <CloudDownload className="text-sm text-white" size={19} />
              </AvatarFallback>
            </Avatar>
            <div className="ml-2 flex-grow overflow-hidden">
              <p className="text-sm font-semibold w-full overflow-hidden text-ellipsis whitespace-nowrap">
                {name}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 opacity-70 font-normal">
                Processing...
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

const File = React.memo(
  ({ file, isSelected, showCheckbox, onSelect, fileUrl }: FileProps) => {
    const { name, thumbUrl, createdAt, owner, duration, parentId, type, _id } =
      file;
    const { name: ownerName = 'Unknown User', picture: ownerPicture = '' } =
      owner || {};
    const [showMenu, setShowMenu] = useState(false);
    const longPressTimer = useRef<ReturnType<typeof setTimeout>>();
    const touchStartTime = useRef<number>(0);
    const [isMobile, setIsMobile] = useState(false);
    const dispatch = useDispatch();

    // Use cached thumbnail hook
    const { thumbnailSrc, isLoading, handleLoad, handleError } =
      useCachedThumbnail({
        fileId: _id,
        thumbnailUrl: thumbUrl,
        fallbackUrl: DefaultThumbnail,
      });

    // Media query effect for mobile detection
    useEffect(() => {
      const mediaQuery = window.matchMedia('(max-width: 640px)');
      setIsMobile(mediaQuery.matches);

      const handleResize = () => setIsMobile(mediaQuery.matches);
      mediaQuery.addEventListener('change', handleResize);

      return () => mediaQuery.removeEventListener('change', handleResize);
    }, []);

    // Check if the file type is Import
    const isImportedFile = type === 'Import';

    const handleSelectionClick = (e: React.MouseEvent) => {
      // Don't allow selecting demo video
      if (isDemoVideo(_id)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (showCheckbox && onSelect) {
        e.preventDefault();
        e.stopPropagation();
        onSelect(file);
      }
    };

    const handleCheckboxChange = () => {
      // Don't allow selecting demo video
      if (isDemoVideo(_id)) {
        return;
      }
      if (showCheckbox && onSelect) {
        onSelect(file);
      }
    };

    const handleTouchStart = () => {
      touchStartTime.current = Date.now();
      longPressTimer.current = setTimeout(() => {
        setShowMenu(true);
      }, 500);
    };

    const handleTouchEnd = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };

    const handleTouchMove = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };

    const parentFolder = useSelector((state: RootState) => {
      return state.library.folders.data.find(
        (folder: FolderType) => folder._id === parentId,
      );
    });

    const handleMoveClick = React.useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(pushModal({ type: 'move-file-modal', files: [file] }));
      },
      [dispatch, file],
    );

    const hasFolder = parentId !== null;

    const { folderId } = useParams<{ folderId: string }>();
    const isInRecentsView = folderId === 'recents';

    if (isImportedFile) {
      return <ProcessingGridView name={name} />;
    }

    // Original card for non-processing files
    return (
      <Card
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onMouseEnter={() => !isMobile && setShowMenu(true)}
        onMouseLeave={() => !isMobile && setShowMenu(false)}
        className={`relative flex flex-col h-full shadow-none bg-transparent border-0 transition-all duration-200 ease-out transform opacity-100 hover:scale-102 sm:hover:scale-102 [&_.hover-menu]:transition-all [&_.hover-menu]:duration-200 [&_.hover-menu]:ease-out ${
          showMenu || isSelected
            ? '[&_.hover-menu]:opacity-100 [&_.hover-menu]:visible'
            : '[&_.hover-menu]:opacity-0 [&_.hover-menu]:invisible sm:[&_.hover-menu]:opacity-0 sm:[&_.hover-menu]:invisible'
        } hover:[&_.hover-menu]:opacity-100 hover:[&_.hover-menu]:visible hover:[&_.hover-duration]:opacity-100 hover:[&_.hover-duration]:visible`}
        onClick={(e) => {
          if (showCheckbox) {
            handleSelectionClick(e);
          }
        }}
      >
        <div className="flex flex-col h-full">
          {showCheckbox && !isDemoVideo(_id) ? (
            <div
              onClick={handleSelectionClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelectionClick(e as any);
                }
              }}
              role="button"
              tabIndex={0}
              className="flex flex-col flex-grow cursor-pointer pointer-events-auto"
            >
              <div className="relative">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className={`absolute top-2 right-2 w-2 h-2 rounded-full bg-orange-500 ${
                          !file.textData?.transcriptCompletedAt
                            ? 'opacity-100 visible'
                            : 'opacity-0 invisible'
                        }`}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Not yet transcribed</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <div
                  className="absolute top-2 left-2 z-[2] bg-white/90 dark:bg-gray-800/90 rounded flex items-center justify-center backdrop-blur-sm transition-all duration-200 ease-out"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectionClick(e);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectionClick(e as any);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={handleCheckboxChange}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4"
                  />
                </div>
                {isLoading && thumbnailSrc !== thumbUrl && (
                  <Skeleton className="w-full aspect-video rounded-lg absolute inset-0 bg-white dark:bg-black" />
                )}
                <img
                  key={_id}
                  src={thumbnailSrc}
                  alt=""
                  loading="eager"
                  decoding="async"
                  className={`card-media w-full aspect-video object-cover rounded-lg transition-opacity duration-200 ease-out ${
                    thumbnailSrc === DefaultThumbnail
                      ? 'dark:invert bg-white'
                      : ''
                  } ${isLoading && thumbnailSrc !== thumbUrl ? 'opacity-0' : 'opacity-100'}`}
                  onLoad={handleLoad}
                  onError={handleError}
                />
                <div
                  className={`hover-duration absolute bottom-2 right-2 px-2 py-1 text-xs font-medium rounded-full backdrop-blur-sm border-0 transition-all duration-200 ease-out ${
                    thumbnailSrc === DefaultThumbnail
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                      : 'bg-black/30 text-white'
                  } opacity-100 visible sm:opacity-0 sm:invisible`}
                >
                  {formatDuration(duration)}
                </div>
              </div>
            </div>
          ) : (
            <a
              href={fileUrl}
              className="flex flex-col flex-grow text-inherit no-underline pointer-events-auto"
            >
              <div className="relative">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className={`absolute top-2 right-2 w-2 h-2 rounded-full bg-orange-500 ${
                          !file.textData?.transcriptCompletedAt
                            ? 'opacity-100 visible'
                            : 'opacity-0 invisible'
                        }`}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Not yet transcribed</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {isLoading && thumbnailSrc !== thumbUrl && (
                  <Skeleton className="w-full aspect-video rounded-lg absolute inset-0 bg-white dark:bg-black" />
                )}
                <img
                  key={_id}
                  src={thumbnailSrc}
                  alt=""
                  loading="eager"
                  decoding="async"
                  className={`card-media w-full aspect-video object-cover rounded-lg transition-opacity duration-200 ease-out ${
                    thumbnailSrc === DefaultThumbnail
                      ? 'dark:invert bg-white'
                      : ''
                  } ${isLoading && thumbnailSrc !== thumbUrl ? 'opacity-0' : 'opacity-100'}`}
                  onLoad={handleLoad}
                  onError={handleError}
                />
                <div
                  className={`hover-duration absolute bottom-2 right-2 px-2 py-1 text-xs font-medium rounded-full backdrop-blur-sm border-0 transition-all duration-200 ease-out ${
                    thumbnailSrc === DefaultThumbnail
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                      : 'bg-black/30 text-white'
                  } opacity-100 visible sm:opacity-0 sm:invisible`}
                >
                  {formatDuration(duration)}
                </div>
              </div>
            </a>
          )}
          <CardContent className="flex-grow flex flex-col justify-start py-4 px-0 bg-transparent">
            <div className="flex items-start justify-between relative">
              <a
                href={fileUrl}
                className="flex items-start w-full overflow-hidden text-inherit no-underline pointer-events-auto"
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Avatar className="w-8 h-8 bg-secondary transition-transform duration-200 ease-out flex-shrink-0 hover:scale-110">
                        <AvatarImage src={ownerPicture} alt={ownerName} />
                        <AvatarFallback className="bg-secondary text-secondary-foreground">
                          {ownerName?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent>{ownerName}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <div className="ml-2 flex-grow overflow-hidden w-full min-h-[4.5rem]">
                  <p
                    data-testid="video-grid-tile-name"
                    className="text-sm font-semibold w-full overflow-hidden line-clamp-2 leading-6 pr-10 break-words"
                  >
                    {name}
                  </p>
                  <div className="pl-px">
                    {isInRecentsView ? (
                      <>
                        <p className="text-xs text-gray-600 dark:text-gray-400 opacity-80 mt-1 mb-0.5 flex items-center">
                          <button
                            type="button"
                            onClick={handleMoveClick}
                            className="flex items-center text-gray-600 dark:text-gray-400 opacity-80 bg-transparent border-0 p-0 cursor-pointer hover:opacity-100 hover:underline"
                          >
                            {hasFolder ? (
                              <Users
                                className="text-xs mr-1 opacity-70 flex items-center"
                                size={8}
                              />
                            ) : (
                              <Lock
                                className="text-xs mr-1 opacity-70 flex items-center"
                                size={8}
                              />
                            )}
                            <span>
                              {parentFolder && parentFolder.name.length > 15
                                ? `${parentFolder.name.slice(0, 15)}...`
                                : parentFolder?.name || 'Private'}
                            </span>
                          </button>
                          <span>&nbsp;•&nbsp;</span>
                          <span data-testid="video-grid-tile-date">
                            {getCreatedAtString(createdAt).toString()}
                          </span>
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-gray-600 dark:text-gray-400 opacity-80 mt-1 flex items-center">
                        {getCreatedAtString(createdAt).toString()}
                      </p>
                    )}
                  </div>
                </div>
              </a>
              <div
                className="hover-menu absolute right-2 top-0 z-[1] bg-white/90 dark:bg-gray-800/90 rounded-full backdrop-blur-sm transition-all duration-200 ease-out opacity-100 visible"
                onClick={(e) => e.preventDefault()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <FileActionMenu file={file} />
              </div>
            </div>
          </CardContent>
        </div>
      </Card>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison for memo - only re-render if these props change
    return (
      prevProps.file._id === nextProps.file._id &&
      prevProps.file.thumbUrl === nextProps.file.thumbUrl &&
      prevProps.file.type === nextProps.file.type &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.showCheckbox === nextProps.showCheckbox &&
      prevProps.fileUrl === nextProps.fileUrl
    );
  },
);

export default File;
